import { useEffect, useRef, useCallback } from "react";

interface UseAutoSearchOptions {
  letters: string[];
  selectedLength: number | null;
  hasSearched: boolean;
  isLoading: boolean;
  onSearch: () => void;
  debounceMs?: number;
  enabled?: boolean;
  hasLinkedConstraints?: boolean;
}

/**
 * Hook that triggers automatic search when letters change.
 * Uses debouncing to avoid excessive searches while typing.
 */
export function useAutoSearch({
  letters,
  selectedLength,
  hasSearched,
  isLoading,
  onSearch,
  debounceMs = 500,
  enabled = true,
  hasLinkedConstraints = false,
}: UseAutoSearchOptions) {
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const previousLettersRef = useRef<string>(letters.join(""));
  const previousLengthRef = useRef<number | null>(selectedLength);
  
  // Track if this is the initial mount
  const isInitialMount = useRef(true);

  const triggerSearch = useCallback(() => {
    if (!isLoading) {
      onSearch();
    }
  }, [isLoading, onSearch]);

  useEffect(() => {
    // Skip on initial mount - don't auto-search when component first loads
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousLettersRef.current = letters.join("");
      previousLengthRef.current = selectedLength;
      return;
    }

    if (!enabled) return;

    const currentLetters = letters.join("");
    const lettersChanged = currentLetters !== previousLettersRef.current;
    const lengthChanged = selectedLength !== previousLengthRef.current;

    // Only auto-search if something actually changed
    if (!lettersChanged && !lengthChanged) return;

    // Update refs
    previousLettersRef.current = currentLetters;
    previousLengthRef.current = selectedLength;

    // Only auto-search if:
    // 1. User has already searched once (hasSearched = true), OR
    // 2. There's enough input to be meaningful (at least 1 letter filled), OR
    // 3. There are linked constraints from other tabs
    const filledLetters = letters.filter(l => l.trim() !== "").length;
    const shouldAutoSearch = hasSearched || filledLetters >= 1 || hasLinkedConstraints;

    if (!shouldAutoSearch) return;

    // Clear existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new debounced search
    debounceRef.current = setTimeout(() => {
      triggerSearch();
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [letters, selectedLength, hasSearched, enabled, debounceMs, triggerSearch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);
}
